/**
 * usePhaseTransition — the single phase-boundary orchestration seam (W5 D15).
 *
 * Before this composable, a speedtest phase boundary (ping → download →
 * upload) was handled by five channels that each behaved differently:
 * the meter needle half-reset its smoother, the under-meter phase bar
 * hard-snapped to 0, the canvas dial rings hard-cut their colour, the
 * timeline handed off with no connective motion. The eye caught the
 * disagreement as "abrupt" — a boundary is only as smooth as its
 * jerkiest channel.
 *
 * This composable retires those independent behaviours into ONE
 * orchestrated ≈600 ms handoff. The shape is a single keyframes.js
 * `ManualTimeline` — the handoff clock — advanced by one keyframes.js
 * `NumericAnimation` managed playback (precept 10: keyframes.js IS the
 * choreography engine; no hand-rolled rAF / setTimeout). Every channel
 * reads `progress` (0→1, eased + boundary-snapped) off that one clock,
 * so a boundary reads as one legible event with a three-beat shape:
 *
 *   (a) the old phase punctuates   — bar drains, needle retreats,
 *                                    timeline tick fires
 *   (b) the colour cross-fades     — rings + `--phase-color` glide
 *   (c) the new phase arrives      — needle advances, bar fills
 *
 * Under `prefers-reduced-motion` the handoff collapses to its end-state
 * instantly — the information (which phase is active) survives; only the
 * motion retires. Same contract `CompleteBadge.vue` already honours.
 *
 * SHARING — the handoff is one event, so the orchestrator is ONE
 * instance: a module-scoped singleton, lazily built, driven off the
 * Pinia store's `currentEnginePhase`. `MeterColumn`, `PhaseTimeline`,
 * `useMeterRenderer`, and `rings.ts` all sample the same surface, so a
 * boundary moves every channel on the one clock. The singleton lives
 * for the page session (the run loop's lifetime); its watcher is
 * installed inside a detached `effectScope` so it survives across any
 * single component's mount/unmount without leaking — the scope is
 * page-lifetime by construction, not component-scoped.
 */

import { effectScope, ref, toRef, watch, type Ref } from "vue";
import { ManualTimeline, NumericAnimation } from "@mkbabb/keyframes.js";
import { useSpeedtestStore } from "@features/speedtest/state/index.ts";
import { easeInOutCubic, linear, parseCssTimeMs } from "@design";

/** The three measured engine phases a handoff can cross. */
export type TransitionPhase = "ping" | "download" | "upload";

function isTransitionPhase(name: string | undefined): name is TransitionPhase {
    return name === "ping" || name === "download" || name === "upload";
}

/**
 * Fallback handoff window — the value the CSS
 * `--motion-duration-phase-handoff` token also declares. Used only when
 * `getComputedStyle` is unavailable (SSR / a non-DOM test runner) so the
 * orchestrator still has a clock.
 */
const PHASE_HANDOFF_FALLBACK_MS = 600;

/**
 * The phase-handoff window, in milliseconds — read ONCE on module-load
 * from the CSS `--motion-duration-phase-handoff` token so the JS
 * `ManualTimeline` and the CSS `--phase-color` / `metric-swap`
 * transitions are clocked off the SAME declared period
 * (AG-A4-HANDOFF-DURATION). Before W3-AG the JS held a literal `600`
 * while the CSS cross-fade ran `--duration-slow` (450 ms) — the chassis
 * hue locked 150 ms before the JS-driven dial finished gliding. The
 * token in `styles/tokens.css` is now the single source of truth; both
 * engines derive from it.
 *
 * Read once (not per-handoff): the token is a static design constant, and
 * `getComputedStyle` on `:root` is a layout-touching call best paid a
 * single time at module init rather than on every phase boundary.
 */
export const PHASE_HANDOFF_MS: number = (() => {
    if (typeof document === "undefined" || !document.documentElement) {
        return PHASE_HANDOFF_FALLBACK_MS;
    }
    const raw = getComputedStyle(document.documentElement).getPropertyValue(
        "--motion-duration-phase-handoff",
    );
    return parseCssTimeMs(raw) ?? PHASE_HANDOFF_FALLBACK_MS;
})();

/**
 * Reactive surface every channel interpolator samples. `progress` is the
 * one shared clock value; `fromPhase` / `toPhase` name the boundary's
 * endpoints so a channel can interpolate between two phase-keyed values.
 */
export interface PhaseTransition {
    /** True while a handoff clock is running. */
    readonly active: Readonly<Ref<boolean>>;
    /** Eased 0→1 handoff progress; 1 (settled) between boundaries. */
    readonly progress: Readonly<Ref<number>>;
    /** Phase being left. `undefined` for the opening `→ ping` arrival. */
    readonly fromPhase: Readonly<Ref<TransitionPhase | undefined>>;
    /** Phase being entered. `undefined` once the run finishes. */
    readonly toPhase: Readonly<Ref<TransitionPhase | undefined>>;
    /** Live, eased clock read for an external paint loop (canvas-side). */
    sample(): number;
}

/**
 * Build the phase-handoff orchestrator over an engine-phase ref.
 *
 * One `ManualTimeline` is the clock. One `NumericAnimation` drives it:
 * `play()` runs keyframes.js's managed rAF and feeds `timeline.set(p)`
 * each frame; the timeline's `easeInOutCubic` pipeline + boundary snap
 * shape the value the channels read. A single watcher on `phase` starts
 * a fresh handoff on every boundary — never five independent watchers.
 *
 * Exported as the testable seam: `usePhaseTransition()` is a module
 * singleton bound to the Pinia store on first call (correct for the
 * page session — see the singleton block below), which makes the
 * orchestrator's behaviour impossible to exercise per-test against a
 * fresh phase source. The factory IS the entire behavioural surface —
 * the singleton wrapper is only lazy memoisation + store wiring — so a
 * spec drives `createPhaseTransition` directly with an injected
 * `Ref<string|undefined>` and never touches the page-lifetime scope.
 */
export function createPhaseTransition(
    phase: Ref<string | undefined>,
): PhaseTransition {
    // The handoff clock. `easeInOutCubic` is the master curve (A4 §3 —
    // a symmetric ease so the punctuate and arrive beats mirror); the
    // boundary snap kills endpoint micro-jitter so `settled` is crisp.
    // Smoothing disabled: the clock is a fixed-shape transition driven
    // by an explicit duration, not a live target chase — the
    // `NumericAnimation` below already owns the timing envelope.
    const timeline = new ManualTimeline({
        easing: easeInOutCubic,
        smoothing: false,
    });
    // Born settled: `set(1)` parks the clock at its end-state and
    // `snap()` collapses any in-flight smoothing so the very first
    // `progress` read between runs is a crisp 1, not a ramp.
    timeline.set(1);
    timeline.snap();

    const active = ref(false);
    const progress = ref(1);
    const fromPhase = ref<TransitionPhase | undefined>(undefined);
    const toPhase = ref<TransitionPhase | undefined>(undefined);

    // The current handoff's driver. Held so a boundary that arrives
    // mid-handoff can cancel the prior play before starting the next.
    let driver: NumericAnimation<{ p: number }> | null = null;

    function settle(): void {
        active.value = false;
        timeline.set(1);
        timeline.snap();
        progress.value = 1;
    }

    function startHandoff(
        from: TransitionPhase | undefined,
        to: TransitionPhase | undefined,
    ): void {
        // Cancel any in-flight handoff — its promise resolves immediately
        // so we never stack two clocks on the one timeline.
        driver?.stop();
        driver = null;

        fromPhase.value = from;
        toPhase.value = to;

        // A handoff runs whenever AT LEAST ONE endpoint is a measured
        // phase — the clock is the single source the four channels share,
        // and the open edges of a run are boundaries too:
        //
        //   • interior (`ping ↔ download ↔ upload`) — both measured: the
        //     full punctuate / cross-fade / arrive three-beat;
        //   • arrival (`undefined → ping`)  — one-sided: the dial rings
        //     cross-fade UP from their phantom-track substrate into the
        //     opening phase, in lockstep with the CSS `--phase-color`
        //     cross-fade off `initial-value` (#888);
        //   • retirement (`upload → undefined`) — one-sided: the rings
        //     cross-fade DOWN from the closing phase back to the phantom
        //     substrate, in lockstep with `--phase-color` returning to
        //     rest before `playCompletion` takes the canvas.
        //
        // Before W3-AG (AG-A4-EDGE-ASYMMETRY) these one-sided edges
        // early-returned `settle()` — the CSS colour cross-faded but the
        // canvas rings hard-cut. Now the clock runs on every edge with a
        // measured side; `drawRingsCrossfade` reads the absent endpoint
        // as the phantom substrate and genuinely cross-fades.
        //
        // Only a NEITHER-measured edge (`undefined → finished`, etc.)
        // carries no boundary at all — settle the clock to steady.
        if (!isTransitionPhase(from) && !isTransitionPhase(to)) {
            settle();
            return;
        }

        active.value = true;
        timeline.reset(0);
        progress.value = 0;

        // The driver: keyframes.js managed playback. `play()` runs the
        // library's own rAF loop (the idiomatic choreography engine —
        // NOT a hand-rolled loop) and feeds the raw 0→1 ramp into the
        // timeline each frame. `respectReducedMotion` snaps the driver
        // to its final keyframe under `prefers-reduced-motion`, so the
        // handoff collapses to its end-state with no motion.
        const play = new NumericAnimation<{ p: number }>([{ p: 0 }, { p: 1 }], {
            duration: PHASE_HANDOFF_MS,
            timingFunction: linear,
            respectReducedMotion: true,
        });
        driver = play;

        void play
            .play(({ p }) => {
                timeline.set(p);
                // `tick()` runs the timeline pipeline (clamp →
                // easeInOutCubic → boundary snap) and returns the eased
                // value the channels consume. Smoothing is off, so
                // `tick()` is pure pipeline.
                progress.value = timeline.tick();
            })
            .then(() => {
                // Play resolved — either the duration elapsed or a newer
                // handoff cancelled this one. Only the un-cancelled,
                // natural completion settles; a cancel leaves the newer
                // handoff's state in place.
                if (driver === play && active.value) {
                    settle();
                }
            });
    }

    // The ONE watcher. Every phase boundary routes through here — the
    // single fan-out point A4 §4 specifies. No channel installs its own
    // boundary watcher any more.
    watch(phase, (next, prev) => {
        startHandoff(
            isTransitionPhase(prev) ? prev : undefined,
            isTransitionPhase(next) ? next : undefined,
        );
    });

    function sample(): number {
        return progress.value;
    }

    return { active, progress, fromPhase, toPhase, sample };
}

// Module-scoped singleton — one handoff orchestrator for the page
// session. Lazily built on first `usePhaseTransition()` call; the watcher
// is installed inside a detached `effectScope` so it survives across
// component mounts/unmounts (the run loop outlives any single component)
// without leaking — the scope is page-lifetime by construction.
let singleton: PhaseTransition | null = null;

/**
 * Access the shared phase-handoff orchestrator. Every channel consumer
 * (`MeterColumn`, `PhaseTimeline`, `useMeterRenderer`, `rings.ts` via the
 * renderer) calls this and samples the one returned surface.
 */
export function usePhaseTransition(): PhaseTransition {
    if (singleton) return singleton;
    const speedtest = useSpeedtestStore();
    // `toRef` re-derives a live `Ref` from the store's unwrapped
    // `currentEnginePhase` getter — the orchestrator's watcher tracks it.
    const phase: Ref<string | undefined> = toRef(
        speedtest,
        "currentEnginePhase",
    );
    const scope = effectScope(true);
    // `scope.run` only returns `undefined` when the scope is already
    // inactive; this scope is freshly constructed one line up, so the
    // run always yields the orchestrator — the non-null assertion is
    // the honest type-narrowing (no `as`-cast laundering precept 6).
    singleton = scope.run(() => createPhaseTransition(phase))!;
    return singleton;
}
